/**
 * Shared rating/review-count display logic for anything that renders a
 * show — the "Nearby Shows" cross-sell card AND the new `/shows` listing
 * page's `ShowCard` both use these, per DOCS/shows/plan.md §5.1's explicit
 * instruction to reuse this logic rather than reimplement it per surface.
 * Originally lived inline in `NearbyShowCard.tsx`; extracted here so both
 * consumers stay byte-identical instead of drifting.
 */

// Ratings 4.0+ round up to a clean display value (4, 4.5, or 5) so the
// stars never show an odd fraction near the top of the scale; below
// 4.0 the raw rating is shown as-is (any non-integer value gets a
// half-star, not just >=0.5 — a deliberate CMS design choice, not a
// rounding bug).
export function getStarValue(rating: number): number {
  if (rating >= 4.8) return 5;
  if (rating >= 4.3) return 4.5;
  if (rating >= 4.0) return 4;
  return rating;
}

// Matches DOCS/shows/listing/brd-backend.md §5's 4 scenarios exactly:
//   <1,000            → exact count, e.g. "532 reviews"
//   1,000–9,999        → floored thousands + "+", e.g. "5K+ reviews"
//   10,000–999,999     → 1-decimal thousands, e.g. "45.7K reviews"
//   ≥1,000,000         → 1-decimal millions, e.g. "1.2M reviews"
export function formatReviewCount(count: number): string {
  const label = count === 1 ? 'review' : 'reviews';
  if (count < 1000) return `${count} ${label}`;
  if (count <= 9999) return `${Math.floor(count / 1000)}K+ reviews`;
  if (count <= 999999) return `${(count / 1000).toFixed(1)}K reviews`;
  return `${(count / 1000000).toFixed(1)}M reviews`;
}
